<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { PhCamera } from "@phosphor-icons/vue"
import axios from 'axios'
import Modal from '@pages/Shared/Modal.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'
import Input from '@pages/Shared/form/Input.vue'
import Select from '@pages/Shared/form/Select.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import userMale from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'

const props = defineProps({
    modelValue: Boolean,
    title:      String,
    status:     String,
    sections:   { type: Array, default: () => [] },
    student:    { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'submitted'])

const fileInput       = ref(null)
const classRooms      = ref([])
const yearOfAdmission = ref(null);
let admissionTimeout  = null

const form = useForm({
    _method:        null,
    id:             null,
    avatar:         null,
    preview:        null,
    first_name:     '',
    last_name:      '',
    other_name:     '',
    email:          '',
    gender:         null,
    dob:            '',
    phone:          '',
    address:        '',
    guardian:       '',
    guardian_phone: '',
    status:         null,
    class_room:     null,
    section:        null,
})

const isCreateMode = computed(() => props.status === 'create')
const isViewMode   = computed(() => props.status === 'view')

const years = computed(() => {
    const current = new Date().getFullYear()
    return Array.from({ length: current - 2011 }, (_, i) => current - i)
})

const sectionOptions  = computed(() =>
    (props.sections || []).map(s => ({ label: s.name, value: s.id }))
)
const classRoomOptions = computed(() =>
    (classRooms.value || []).map(c => ({ label: c.name, value: c.id }))
)

watch(() => props.modelValue, async (open) => {
  if (!open) return
  await nextTick()

  if (isCreateMode.value) {
    form.reset()
    form.clearErrors()
    classRooms.value      = []
    yearOfAdmission.value = new Date().getFullYear()  
    return
  }

  if (!props.student) return

  form.clearErrors()
  form.id             = props.student.id
  form.avatar         = props.student.avatar
  form.preview        = null
  form.first_name     = props.student.first_name
  form.last_name      = props.student.last_name
  form.other_name     = props.student.other_name
  form.email          = props.student.email
  form.gender         = props.student.gender
  form.dob            = props.student.dob
  form.phone          = props.student.phone
  form.address        = props.student.address
  form.guardian       = props.student.guardian
  form.guardian_phone = props.student.guardian_phone
  form.status         = props.student.status
  form.section        = props.student.class_room?.section?.id ?? null
  form.class_room     = props.student.class_room?.id ?? null

  classRooms.value = props.student.class_room?.section?.classes || []
}, { immediate: true })

watch([() => form.section, yearOfAdmission], async ([sectionId, year]) => {
    if (sectionId) {
        const section    = props.sections.find(s => s.id === sectionId)
        classRooms.value = section?.classes || []

        if (isCreateMode.value) {
            form.class_room = null
        }
    }

    if (!isCreateMode.value || !sectionId || !year) return

    clearTimeout(admissionTimeout)
    admissionTimeout = setTimeout(async () => {
        const { data } = await axios.post('/staff/manage-students/admission', {
            section: sectionId, year,
        })
        form.id = data.admission
    }, 300)
})

const handleFileSelected = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (form.preview) URL.revokeObjectURL(form.preview)
    form.avatar  = file
    form.preview = URL.createObjectURL(file)
}

const handleSubmit = () => emit('submitted', form)

const handleModalClose = () => {
    if (form.preview) URL.revokeObjectURL(form.preview)
    form.clearErrors()
    form.reset()
    classRooms.value      = []
    yearOfAdmission.value = new Date().getFullYear()  
}
</script>

<template>
    <Modal
        :model-value="modelValue"
        :title="title"
        @update:modelValue="emit('update:modelValue', $event)"
        @close="handleModalClose"
    >
        <form class="p-4 md:p-5" @submit.prevent="handleSubmit">

            <!-- Avatar -->
            <div class="relative w-28 h-28 mx-auto my-4">
                <img
                    :src="form.preview
                        ? form.preview
                        : (form.avatar
                            ? `/storage/${form.avatar}`
                            : (form.gender === 'Male' ? userMale : userFemale))"
                    class="w-full h-full object-cover rounded-full ring-4 ring-gray-100"
                    alt="Student avatar"
                />
                <span
                    v-if="!isViewMode"
                    class="absolute bottom-0 right-0 p-1.5 rounded-full text-white cursor-pointer transition"
                    :class="status === 'edit'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary-700 hover:bg-primary-800'"
                    @click="fileInput?.click()"
                >
                    <PhCamera :size="16" />
                </span>
                <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleFileSelected"
                />
            </div>

            <Input v-model="form.first_name" label="First Name" placeholder="Enter first name"
                :disabled="isViewMode" :error="form.errors.first_name" class="mb-4" />

            <Input v-model="form.last_name" label="Last Name" placeholder="Enter last name"
                :disabled="isViewMode" :error="form.errors.last_name" class="mb-4" />

            <Input v-model="form.other_name" label="Other Name" placeholder="Enter other name (Optional)"
                :disabled="isViewMode" :error="form.errors.other_name" class="mb-4" />

            <Input v-model="form.email" type="email" label="Email" placeholder="Email address"
                :disabled="isViewMode" :error="form.errors.email" class="mb-4" />

            <Select v-model="form.gender" label="Gender" :options="['Male', 'Female']"
                placeholder="Select Gender" :disabled="isViewMode"
                :error="form.errors.gender" class="mb-4" />

            <Input v-model="form.dob" type="date" label="Date of Birth"
                :disabled="isViewMode" :error="form.errors.dob" class="mb-4" />

            <Input v-model="form.phone" type="text" label="Phone" placeholder="Phone Number"
                :disabled="isViewMode" :error="form.errors.phone" class="mb-4" />

            <Textarea v-model="form.address" label="Address" placeholder="Enter address..."
                :rows="2" :disabled="isViewMode" :error="form.errors.address" class="mb-4" />

            <Input v-model="form.guardian" label="Guardian" placeholder="Guardian Name"
                :disabled="isViewMode" :error="form.errors.guardian" class="mb-4" />

            <Input v-model="form.guardian_phone" label="Guardian Phone" placeholder="Guardian Phone"
                :disabled="isViewMode" :error="form.errors.guardian_phone" class="mb-4" />

            <Select v-if="isCreateMode" v-model="yearOfAdmission" label="Year of Admission"
                :options="years" placeholder="Select year" class="mb-4" />

            <Select v-model="form.section" label="Section" :options="sectionOptions"
                placeholder="Select section" :disabled="isViewMode"
                :error="form.errors.section" class="mb-4" />

            <Select v-model="form.class_room" label="Class" :options="classRoomOptions"
                placeholder="Select class" :disabled="isViewMode || !form.section"
                :error="form.errors.class" class="mb-4" />

            <Input v-model="form.id" type="text" label="Admission Number"
                placeholder="Auto Generated" :disabled="true"
                :error="form.errors.id" class="mb-4" />

            <Select v-if="!isCreateMode" v-model="form.status" label="Status"
                :options="[
                    { label: 'Active',    value: 'active'    },
                    { label: 'Inactive',  value: 'inactive'  },
                    { label: 'Graduated', value: 'graduated' },
                ]"
                placeholder="Select status" :disabled="isViewMode"
                :error="form.errors.status" class="mb-4" />

            <button
                v-if="!isViewMode"
                type="submit"
                class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
                       transition flex justify-center items-center gap-2"
                :class="status === 'edit'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-primary-700 hover:bg-primary-800'"
                :disabled="form.processing"
            >
                <Spinner v-if="form.processing" :size="14" />
                {{ status === 'edit' ? 'Update Student' : 'Add Student' }}
            </button>

        </form>
    </Modal>
</template>